
  import { Vue, Component, Prop, Watch } from "vue-property-decorator";
  import { cloneDeep, isEqual } from "lodash-es";

  import LIST_COMPANY_POSTINGS from "@/apollo/jobboard/ListCompanyPostings.gql";

  import PostingModal from "@/components/postings/PostingModalV2.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import HoveredIcon from "@/components/icons/HoveredIcon.vue";
  import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
  import ScaffoldHeader from "@/components/scaffold/ScaffoldHeader.vue";
  import PostingCard from "@/components/recruitings/PostingCardV2.vue";
  import CardPagination from "@/components/card/CardPagination.vue";
  import PostingFilters from "@/components/recruitings/PostingFiltersV2.vue";
  import PlusIcon from "@/components/icons/PlusIconNew.vue";
  import ActionPanel from "@/components/common/ActionsPanel.vue";
  import { getFiltersDataFromQuery, joinQueryValues } from "@/helpers/router/prepareFiltersQuery";

  import paginationConfig from "@/data/paginationConfig";
  import managementPostingsFiltersTemplate from "@/data/filtersTemplates/managementPostingsFiltersTemplate";
  import Roles from "@/entities/Roles";
  import PaginationConfig from "@/entities/PaginationConfig";
  import { Company, Posting, User } from "@/generated/graphql";
  import ManagementPostingsFiltersQuery from "@/entities/filters/ManagementPostingsFiltersQuery";
  import { getPaginationDataFromQuery, getPaginationQuery } from "@/helpers/router/preparePaginationQuery";
  import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessages";
  import EmptyListImagePosting from "@/components/icons/emptyListImages/EmptyListImagePosting.vue";
  import { ApplicantStatus } from "@/entities/ApplicantStatus";
  import getStateQueryConditions from "@/helpers/locations/stateQueryConditions";

  @Component({
    components: {
      EmptyListImagePosting,
      PostingModal,
      DefaultContentLayout,
      EmptyListPlaceholder,
      HoveredIcon,
      PostingFilters,
      ScaffoldHeader,
      LoadingOverlay,
      UiButton,
      CardPagination,
      PostingCard,
      PlusIcon,
      ActionPanel,
    },
    apollo: {
      postings: {
        query: LIST_COMPANY_POSTINGS,
        variables() {
          return this.postingFilter;
        },
        update(data) {
          this.paginationConfig.total = data.total.aggregate.count;
          this.paginationConfig.loadedCount = data.posting.length;
          return data.posting;
        },
      },
    },
  })
  export default class RecrutingPostings extends Vue {
    @Prop({ required: true, type: Object }) readonly currentUser!: User;
    @Prop({ required: true, type: Object }) readonly company!: Company;

    postings: Posting[] | null = null;

    dialog: boolean = false;

    paginationConfig: PaginationConfig = { ...paginationConfig };

    postingFilters: ManagementPostingsFiltersQuery = cloneDeep(managementPostingsFiltersTemplate);

    get loading(): boolean {
      return this.$apollo.queries.postings.loading;
    }

    get hasData(): boolean {
      return !this.loading && Boolean(this.postings);
    }

    get filtersEmpty(): boolean {
      return isEqual(this.postingFilters, managementPostingsFiltersTemplate);
    }

    get emptyListPlaceholder(): string {
      const entitiesName = "Job Postings";

      if (this.filtersEmpty) {
        return `You currently do not have any ${entitiesName}`;
      }

      return getEmptyFiltersResultMessage(entitiesName);
    }

    get empty(): boolean {
      if (!this.postings) {
        return false;
      }
      return this.postings.length === 0;
    }

    get managerId(): string | undefined {
      return this.currentUser.manager?.id;
    }

    get isManager(): boolean {
      return this.currentUser.role === Roles.MANAGER;
    }

    hanldeRefetch(): void {
      this.$apollo.queries.postings.refetch();
    }
    get postingFilter(): any {
      const { page, limit } = this.paginationConfig;
      const nextOffset = page * limit - limit;

      const {
        postedAt,
        managers,
        countries,
        states,
        cities,
        industries,
        contractors,
        minDuration,
        maxDuration,
        jobTitle,
        skills,
      } = this.postingFilters;

      const filterConditions = [];

      const applicationWhere = {
          _and: [
      { is_cancelled: { _eq: false } },
      {
        _or: [
          {
            status: {
              _in: [
                ApplicantStatus.INTERVIEWED,
                ApplicantStatus.APPROVED,
                ApplicantStatus.SEND_FOR_APPROVAL,
                ApplicantStatus.DECLINED,
                ApplicantStatus.UNDER_REVIEW
              ]
            }
          },
          {
            status: {
              _eq: ApplicantStatus.NOT_REVIEWED
            }
          }
        ]
      }
    ]
};
      if (jobTitle) {
        filterConditions.push({
          job_title: { _ilike: `%${jobTitle}%` },
        });
      }

      if (countries.length) {
        filterConditions.push({
          country: { _in: countries },
        });
      }

      if (states.length) {
        filterConditions.push({
          state: getStateQueryConditions({countries, states}),
        });
      }

      if (cities.length) {
        filterConditions.push({
          city: { _in: cities },
        });
      }

      if (skills.length) {
        filterConditions.push({
          skills: {
            skill_id: { _in: skills },
          },
        });
      }

      if (industries.length) {
        filterConditions.push({
          industries: {
            industry: {
              name: { _in: industries },
            },
          },
        });
      }

      if (contractors.length) {
        filterConditions.push({
          applications: {
            user: {
              contracts: {
                id: { _in: contractors },
              },
            },
          },
        });
      }

      if (managers.length) {
        filterConditions.push({
          manager: {
            id: { _in: managers },
          },
        });
      }

      if (postedAt) {
        let active = false;
        let publicPosting = false;

        if (postedAt === "marketplace") {
          active = true;
          publicPosting = true;
        }

        if (postedAt === "internal") {
          active = true;
        }

        filterConditions.push({
          _and: [{ active: { _eq: active } }, { public: { _eq: publicPosting } }],
        });
      }

      if (minDuration || maxDuration) {
        const duration: any = {};

        if (maxDuration) {
          duration._lte = maxDuration;
        }

        if (minDuration) {
          duration._gte = minDuration;
        }

        filterConditions.push({ duration });
      }

      return {
        offset: nextOffset,
        limit: limit,
        companyId: this.company.id,
        where: { _and: filterConditions },
        applicationWhere,
      };
    }

    get queryPropsToWatch(): string {
      return JSON.stringify([
        this.postingFilters,
        this.paginationConfig,
      ]);
    }

    get queryParams(): {filters: ManagementPostingsFiltersQuery, pagination: PaginationConfig} {
      const query = this.$route.query;
      const {
        job_title: jobTitle,
        min_duration: minDuration,
        max_duration: maxDuration,
        posted_at: postedAt,
        ...queryParams
      } = query;

      const queryData = {
        jobTitle,
        minDuration,
        maxDuration,
        postedAt,
        ...queryParams,
      };

      const filters = getFiltersDataFromQuery<ManagementPostingsFiltersQuery>(managementPostingsFiltersTemplate, queryData);
      const pagination = {...this.paginationConfig, ...getPaginationDataFromQuery(query)};

      return {
        filters,
        pagination,
      };
    }

    @Watch("queryPropsToWatch")
    updateQuery(): void {
      const paginationQuery = getPaginationQuery(this.paginationConfig);

      const {
        jobTitle,
        minDuration,
        maxDuration,
        postedAt,
        ...filtersData
      } = this.postingFilters;

      const query = joinQueryValues({
        ...paginationQuery,
        ...filtersData,
        posted_at: postedAt,
        job_title: jobTitle,
        min_duration: minDuration,
        max_duration: maxDuration,
      });

      if (isEqual(query, this.$route.query)) {
        return;
      }

      this.$router.push({query}).catch(()=>{});
    }

    @Watch("$route.query", { immediate: true })
    onQueryChange(): void {
      const { filters, pagination } = this.queryParams;

      this.postingFilters = filters;
      this.paginationConfig = pagination;
    }

    updateFilters(updatedFilters: ManagementPostingsFiltersQuery) {
      const {
        jobTitle,
        skills,
        industries,
        contractors,
        countries,
        states,
        cities,
        managers,
        postedAt,
        minDuration,
        maxDuration,
      } = updatedFilters;

      const dataEqual = isEqual(this.postingFilters, {
        jobTitle,
        skills,
        industries,
        contractors,
        countries,
        states,
        cities,
        managers,
        postedAt,
        minDuration,
        maxDuration,
      });

      if (dataEqual) {
        return;
      }

      const query = joinQueryValues({
        skills,
        industries,
        contractors,
        countries,
        states,
        cities,
        managers,
        job_title: jobTitle,
        posted_at: postedAt,
        min_duration: minDuration,
        max_duration: maxDuration,
      });

      this.$router.push({query}).catch(()=>{});
    }

    resetFilters(): void {
      if (isEqual(this.postingFilters, managementPostingsFiltersTemplate)) {
        return;
      }

      this.postingFilters = cloneDeep(managementPostingsFiltersTemplate);
      this.paginationConfig = { ...paginationConfig };
    }

    toggle(): void {
      this.dialog = true;
    }

    canUserChangePosting(posting: Posting) {
      const isAdmin = this.currentUser.role === Roles.ADMIN;
      const isManagerPosting = posting?.manager?.user.id === this.currentUser.id;
      return isAdmin || isManagerPosting;
    }
  }
